import {
  FINANCE_PRODUCTS_FR,
  FINANCE_PRODUCTS_UK,
} from '../../Constants/Filters/PriceFilter.constants';
import type { DealershipAttrs } from '../../Entities/Dealership/Dealership.entity';
import type { Locale } from '../../Entities/Locale/Locale.entity';
import type { LocationAttrs } from '../../Entities/Location/LocationAttr.entity';
import type { AggregationsState } from '../../Entities/Search/Aggregations/Aggregations.entity';
import type { FilterValueDisplayObj } from '../../Entities/Search/SearchFilters/FilterValueDisplayObj.entity';
import { getFormattedCurrency } from '../../Utils/Intl/GetFormattedCurrency';
import { mappedLocales } from '../../Utils/Intl/MappedLocales';
import { slugPartToWords } from '../../Utils/Search/SlugPartToWords';
import { capitalCase } from '../../Utils/Strings/CapitalCase';

type UKFinanceProducts = 'pcp' | 'hp' | 'ctp';
type FRFinanceProducts = 'privateLeasing' | 'standardFinancing';

export type FilterStrings = {
  filtersDoorsUnit: string;
  filtersEmissionUnit: string;
  minBhp: string;
  maxBhp: string;
  bhp: string;
  kw: string;
  filtersEngineSizeUnit: string;
  filtersEvRangeUnit: string;
  filtersEvRangeMinLabel: string;
  filtersAny: string;
  within: string;
  minMileage: string;
  maxMileage: string;
  filtersDistanceUnit: string;
  minPrice: string;
  'stock-condition-new': string;
  'stock-condition-used': string;
  maxPrice: string;
  filtersSeatsUnit: string;
  otherColors: string;
  onlyOnHeyCar: string;
  heyConExclusive: string;
  hideSold: string;
  newCarEu24: string;
};
export const getFilterValueDisplayName = (
  filter: FilterValueDisplayObj,
  strings: FilterStrings,
  aggregations: Pick<AggregationsState, 'makes' | 'models' | 'variants'>,
  locale: Locale,
  locationAttrs?: LocationAttrs,
  dealerAttrs?: DealershipAttrs,
) => {
  const value = filter.value!;

  const locationAttrsObj = locationAttrs || { regions: undefined, areas: undefined };

  const { regions, areas } = locationAttrsObj;

  // Some values need custom formatting
  if (filter.id === 'doors') return `${value} ${strings.filtersDoorsUnit}`;

  if (filter.id === 'emission-sticker') return `${strings.filtersEmissionUnit} ${value}`;

  if (filter.id === 'engine-power')
    return filter.valueGte === filter.valueLte
      ? `${filter.valueGte || strings.minBhp} ${strings.bhp}`
      : `${filter.valueGte || strings.minBhp} - ${filter.valueLte || strings.maxBhp} ${
          strings.bhp
        }`;

  if (filter.id === 'engine-size')
    return filter.valueGte === filter.valueLte
      ? `${filter.valueGte ? filter.valueGte + strings.filtersEngineSizeUnit : strings.filtersAny}`
      : `${
          filter.valueGte ? filter.valueGte + strings.filtersEngineSizeUnit : strings.filtersAny
        } - ${
          filter.valueLte ? filter.valueLte + strings.filtersEngineSizeUnit : strings.filtersAny
        }`;

  if (filter.id === 'max-distance')
    return `${strings.within} ${value} ${strings.filtersDistanceUnit}`;

  if (filter.id === 'ev-wltp-range')
    return `${filter.valueGte || strings.filtersAny} ${strings.filtersEvRangeUnit} ${
      strings.filtersEvRangeMinLabel
    }`;

  if (filter.id === 'mileage')
    return `${
      filter.valueGte
        ? Intl.NumberFormat(mappedLocales[locale]).format(Number(filter.valueGte))
        : strings.minMileage
    } - ${
      filter.valueLte
        ? Intl.NumberFormat(mappedLocales[locale]).format(Number(filter.valueLte))
        : strings.maxMileage
    } ${strings.filtersDistanceUnit}`;

  if (filter.id === 'stock-condition')
    return strings[`stock-condition-${value}` as keyof FilterStrings];

  if (filter.id === 'price' || filter.id === 'monthly-price')
    return `${
      filter.valueGte ? getFormattedCurrency(Number(filter.valueGte), locale) : strings.minPrice
    } - ${
      filter.valueLte ? getFormattedCurrency(Number(filter.valueLte), locale) : strings.maxPrice
    }`;
  if (filter.id === 'postcode') return `${filter.postcode!}`;

  if (filter.id === 'region') {
    return filter['nuts-label']!
      ? `${filter['nuts-label']!}`
      : `${
          regions?.find(
            region => filter.value === (locale === 'uk' ? region?.id : region?.regionCode),
          )?.shortName
        }`;
  }

  if (filter.id === 'area') {
    return `${
      areas?.find(area => filter?.value === (locale === 'uk' ? area?.area : area?.areaCode))
        ?.shortName
    }`;
  }

  if (filter.id === 'dealership-id') {
    return capitalCase((dealerAttrs?.displayName || dealerAttrs?.name)?.toLowerCase() || '');
  }

  if (filter.id === 'seats') return `${value} ${strings.filtersSeatsUnit}`;

  if (filter.value === 'others' && filter.id === 'color') {
    return strings.otherColors;
  }

  if (filter.id === 'make') {
    const makeData = aggregations.makes.filter(make => make.key === value);
    return makeData[0] ? makeData[0].displayName : slugPartToWords(value);
  }

  if (filter.id === 'model') {
    const modelData = aggregations.models.filter(model => model.key === value);
    return modelData[0] ? modelData[0].displayName : slugPartToWords(value);
  }

  if (filter.id === 'variant-type') {
    const variantData = aggregations.variants.filter(variant => variant.key === value);
    const variantIdParts = value.split('-');
    return variantData[0] ? variantData[0].displayName : slugPartToWords(variantIdParts[2] ?? '');
  }

  if (filter.id === 'finance-product') {
    const value = filter.value;
    if (locale === 'uk' && value) {
      return FINANCE_PRODUCTS_UK[value as UKFinanceProducts].label;
    } else if (locale === 'fr' && value) {
      return FINANCE_PRODUCTS_FR[value as FRFinanceProducts].label;
    }
  }

  if (filter.id === 'eligible-products') {
    if (locale === 'fr') return strings.heyConExclusive;
    if (locale === 'uk') return strings.onlyOnHeyCar;
    return null;
  }

  if (filter.id === 'vehicle-status') {
    return strings.hideSold;
  }

  if (filter.id === 'fuel-consumption')
    return `${filter.valueGte || '0'} - ${filter.valueLte || '20'} l/100km`;

  // Generic formatting
  if (filter.value === 'true') {
    return strings[filter.id];
  }

  if (filter.valueGte || filter.valueLte) {
    return filter.valueGte === filter.valueLte
      ? filter.valueGte || strings.filtersAny
      : `${filter.valueGte || strings.filtersAny} - ${filter.valueLte || strings.filtersAny}`;
  }

  const intlValue = strings[value];

  return capitalCase(intlValue ? intlValue : filter.value || '');
};
